import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@secretaa/ui";
import { LogOut, Menu, Search, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, NavLink, Outlet, useNavigate, useParams } from "react-router";
import { api } from "../app/api.ts";
import { useCurrentWorkspace, useSession } from "../app/session.ts";
import { isAuthError } from "../shared/errors.ts";
import { LocaleSwitch } from "./locale-switch.tsx";
import { visibleSections } from "./sidebar-registry.ts";

export function WorkspaceLayout() {
  const { t } = useTranslation();
  const { workspaceId = "" } = useParams();
  const session = useSession();
  const current = useCurrentWorkspace();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  if (session.isPending) return <p className="p-6">{t("app.loading")}</p>;
  if (session.isError) return isAuthError(session.error) ? <Navigate to="/login" replace /> : <p className="p-6">{t("errors.generic")}</p>;
  if (!current) return <Navigate to="/select-workspace" replace />;
  const sections = visibleSections(current.role);
  return (
    <div className="flex min-h-full flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:p-2">
        {t("app.skipToContent")}
      </a>
      <header className="flex min-h-14 flex-wrap items-center gap-3 py-2 border-b border-(--saa-border) bg-(--saa-surface) px-4">
        <Button variant="ghost" className="md:hidden" aria-label={t("nav.menu")} aria-expanded={mobileNavOpen} aria-controls="workspace-nav" onClick={() => setMobileNavOpen(!mobileNavOpen)}><Menu size={18} aria-hidden /></Button>
        <span className="font-semibold">{t("app.name")}</span>
        <label className="flex items-center gap-1 text-sm">
          <span className="sr-only">{t("workspace.switcher")}</span>
          <select
            className="max-w-36 rounded-md sm:max-w-48 border border-(--saa-border) bg-(--saa-surface) px-2 py-1"
            value={workspaceId}
            onChange={(e) => navigate(`/w/${e.target.value}/overview`)}
          >
            {session.data.memberships.map((m) => (
              <option key={m.workspaceId} value={m.workspaceId}>
                {m.workspaceName}
              </option>
            ))}
          </select>
        </label>
        {/* Slot tìm kiếm / lệnh (P0 để trống, không render chức năng giả). */}
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden items-center gap-1 text-sm text-(--saa-fg-muted) sm:flex" aria-hidden>
            <Search size={14} />
          </span>
          <LocaleSwitch />
          <Link to="/account" aria-label={t("nav.account")} className="rounded-md p-2 hover:bg-(--saa-muted)">
            <UserRound size={18} aria-hidden />
          </Link>
          <Button
            variant="ghost"
            aria-label={t("auth.logout")}
            onClick={async () => {
              await api.auth.logout().catch(() => undefined);
              qc.clear();
              navigate("/login");
            }}
          >
            <LogOut size={16} aria-hidden />
          </Button>
        </div>
      </header>
      <div className="flex min-w-0 flex-1 flex-col md:flex-row">
        <nav id="workspace-nav" aria-label={t("nav.mainNav")} className={`${mobileNavOpen ? "block" : "hidden"} w-full shrink-0 border-r border-(--saa-border) bg-(--saa-surface) p-3 md:block md:w-56`}>
          {sections.map((s) => (
            <div key={s.id} className="mb-4">
              {s.labelKey ? <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-(--saa-fg-muted)">{t(s.labelKey)}</p> : null}
              <ul className="flex flex-col gap-0.5">
                {s.items.map((item) => (
                  <li key={item.id}>
                    <NavLink
                      to={item.to(workspaceId)}
                      onClick={() => setMobileNavOpen(false)}
                      className={({ isActive }) => `flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${isActive ? "bg-(--saa-muted) font-medium" : "hover:bg-(--saa-muted)"}`}
                    >
                      <item.icon size={16} aria-hidden />
                      {t(item.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <main id="main" className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
        {/* Cột phải dành cho panel AI (P4) — P0 không render. */}
      </div>
    </div>
  );
}
